import clsx from "clsx";
import { useState } from "react";
import Image from "next/image";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";

import { getDownloads } from "../lib/downloads";
import { resolveAssetUrl } from "../utils/resolve-asset-url";
import useViewport, { Viewport } from "../hooks/useViewport";

import Button from "../components/button";
import Divider from "../components/divider";
import Heading from "../components/heading";
import Hero from "../components/hero";
import CarouselButtons from "../components/carousel-buttons";

import styles from "./downloads.module.scss";

import SeoMeta from "../components/seo-meta";
import config from "../config";
import { getSEOPageProps } from "../lib/seo";

const LogoType = ({ epsDownload, pngDownload, large }) => {
  const width = large ? 321 : 196;
  const height = large ? 278 : 74;

  return (
    <div className={clsx(styles.LogoType, large && styles.LogoTypeLarge)}>
      <div className={styles.Image}>
        <Image
          src={epsDownload?.imagePreview?.url}
          alt={epsDownload?.imagePreview?.title}
          width={width}
          height={height}
          layout="responsive"
        />
      </div>
      <div className={styles.DownloadLinks}>
        {epsDownload.media?.url && (
          <a
            href={epsDownload.media?.url}
            download
            target="_blank"
            rel="noreferrer"
          >
            Download .eps for print
          </a>
        )}
        {pngDownload.media?.url && (
          <a
            href={pngDownload.media?.url}
            download
            target="_blank"
            rel="noreferrer"
          >
            Download .png for screen
          </a>
        )}
      </div>
    </div>
  );
};

const Wallpaper = ({ download, phone }) => {
  const width = phone ? 112 : 289;
  const height = phone ? 228 : 228;

  const device = useViewport();

  const downloadLabel =
    device === Viewport.PHONE ? "Download" : "View/Download";

  return (
    <div className={styles.Wallpaper}>
      <div className={styles.Image}>
        <Image
          src={download?.imagePreview?.url}
          alt={download?.imagePreview?.title}
          width={width}
          height={height}
        />
      </div>
      {download.media?.url && (
        <Button mini outline rounded>
          <a
            href={resolveAssetUrl(download.media?.url)}
            download
            target="_blank"
            rel="noreferrer"
          >
            {downloadLabel}
          </a>
        </Button>
      )}
    </div>
  );
};

const Brochure = ({ download }) => {
  if (!download) return null;

  return (
    <div className={styles.Brochure}>
      <div className={styles.Image}>
        <Image
          src={download?.imagePreview?.url}
          width="170"
          height="230"
          alt={download?.imagePreview?.title}
        />
      </div>
      <div className={styles.Content}>
        <h3>{download.name}</h3>
        <p>{download.description}</p>
        {download.media?.url && (
          <Button mini outline rounded>
            <a
              href={resolveAssetUrl(download.media?.url)}
              download
              target="_blank"
              rel="noreferrer"
            >
              View/Download
            </a>
          </Button>
        )}
      </div>
    </div>
  );
};

const DesktopDownloadsPage = ({ downloads }) => {
  const brochureSections = [
    {
      title: "Solutions Brochures",
      brochures: [
        downloads["open-space"],
        downloads["private-space"],
        downloads["spaces-for-hire"]
      ].filter((item) => item && item.media)
    },
    {
      title: "Programming Brochures",
      brochures: [
        downloads["a-little-night-music"],
        downloads["east-room-multiples"],
        downloads["work-redux-mix"]
      ].filter((item) => item && item.media)
    },
    {
      title: "Sponsorship Package",
      brochures: [downloads["event-sponsorship"]].filter(
        (item) => item && item.media
      )
    }
  ];

  return (
    <div className="container">
      <Hero title="Downloads" />

      <section className={styles.DownloadSection}>
        <div className={styles.DownloadHeading}>
          <Heading>Logo Types</Heading>
        </div>
        <Divider />
        <div className={styles.DownloadContent}>
          <div className={styles.LogoTypes}>
            <div className={styles.Logos}>
              <LogoType
                epsDownload={downloads["white-logo-type-eps"]}
                pngDownload={downloads["white-logo-type-png"]}
              />
              <LogoType
                epsDownload={downloads["black-logo-type-eps"]}
                pngDownload={downloads["black-logo-type-png"]}
              />
            </div>

            <LogoType
              epsDownload={downloads["mascot-eps"]}
              pngDownload={downloads["mascot-png"]}
              large
            />
            <LogoType
              epsDownload={downloads["mascot-logo-eps"]}
              pngDownload={downloads["mascot-logo-png"]}
              large
            />
          </div>
        </div>
      </section>

      <section className={styles.DownloadSection}>
        <div className={styles.DownloadHeading}>
          <Heading>Wallpapers</Heading>
        </div>
        <Divider />
        <div className={styles.DownloadContent}>
          <div className={styles.WallpaperDesktop}>
            <Wallpaper download={downloads["black-wallpaper"]} />
            <Wallpaper download={downloads["white-wallpaper"]} />
          </div>
          <div className={styles.WallpaperPhone}>
            <Wallpaper phone download={downloads["black-phone"]} />
            <Wallpaper phone download={downloads["white-phone"]} />
            <Wallpaper phone download={downloads["mascot-phone"]} />
          </div>
        </div>
      </section>

      {brochureSections.map((brochureSection) => {
        if (brochureSection.brochures.length <= 0) {
          return null;
        }

        return (
          <section
            key={brochureSection.title}
            className={styles.DownloadSection}
          >
            <div className={styles.DownloadHeading}>
              <Heading>{brochureSection.title}</Heading>
            </div>
            <Divider />
            <div className={styles.DownloadContent}>
              {brochureSection.brochures.map((brochure) => (
                <Brochure key={brochure.title} download={brochure} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

const LogoTypesSlider = ({ downloads }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    downloads["white-logo-type-eps"],
    downloads["mascot-eps"],
    downloads["mascot-logo-eps"]
  ];

  return (
    <>
      <div className={styles.MobileCarousel}>
        <Carousel
          showArrows={false}
          showStatus={false}
          infiniteLoop={false}
          showIndicators={false}
          autoPlay={false}
          selectedItem={currentSlide}
          swipeScrollTolerance={20}
          preventMovementUntilSwipeScrollTolerance
          onChange={(index) => setCurrentSlide(index)}
        >
          <div className={styles.Slide}>
            <LogoType
              epsDownload={downloads["white-logo-type-eps"]}
              pngDownload={downloads["white-logo-type-png"]}
            />
            <LogoType
              epsDownload={downloads["black-logo-type-eps"]}
              pngDownload={downloads["black-logo-type-png"]}
            />
          </div>
          <div className={styles.Slide}>
            <LogoType
              epsDownload={downloads["mascot-eps"]}
              pngDownload={downloads["mascot-png"]}
              large
            />
          </div>
          <div className={styles.Slide}>
            <LogoType
              epsDownload={downloads["mascot-logo-eps"]}
              pngDownload={downloads["mascot-logo-png"]}
              large
            />
          </div>
        </Carousel>
      </div>
      <CarouselButtons
        slides={slides.map((slide, index) => ({
          active: index === currentSlide
        }))}
        onChange={(index) => setCurrentSlide(index)}
      />
    </>
  );
};

const WallpaperSlider = ({ downloads }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    downloads["black-wallpaper"],
    downloads["white-wallpaper"],
    downloads["black-phone"]
  ];

  return (
    <>
      <div className={styles.MobileCarousel}>
        <Carousel
          showArrows={false}
          showStatus={false}
          infiniteLoop={false}
          showIndicators={false}
          autoPlay={false}
          axis="horizontal"
          verticalSwipe="natural"
          swipeScrollTolerance={20}
          preventMovementUntilSwipeScrollTolerance
          selectedItem={currentSlide}
          onChange={(index) => setCurrentSlide(index)}
        >
          <div className={styles.Slide}>
            <div className={styles.WallpaperDesktop}>
              <Wallpaper download={downloads["black-wallpaper"]} />
            </div>
          </div>
          <div className={styles.Slide}>
            <div className={styles.WallpaperDesktop}>
              <Wallpaper download={downloads["white-wallpaper"]} />
            </div>
          </div>
          <div className={styles.Slide}>
            <div className={styles.WallpaperPhone}>
              <Wallpaper phone download={downloads["black-phone"]} />
              <Wallpaper phone download={downloads["white-phone"]} />
              <Wallpaper phone download={downloads["mascot-phone"]} />
            </div>
          </div>
        </Carousel>
      </div>
      <CarouselButtons
        slides={slides.map((slide, index) => ({
          active: index === currentSlide
        }))}
        onChange={(index) => setCurrentSlide(index)}
      />
    </>
  );
};

const BrochureSlider = ({ downloads }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = downloads;

  return (
    <>
      <div className={styles.MobileCarousel}>
        <Carousel
          showArrows={false}
          showStatus={false}
          infiniteLoop={false}
          showIndicators={false}
          autoPlay={false}
          axis="horizontal"
          verticalSwipe="natural"
          swipeScrollTolerance={20}
          preventMovementUntilSwipeScrollTolerance
          selectedItem={currentSlide}
          onChange={(index) => setCurrentSlide(index)}
        >
          {slides.map((slide, index) => (
            <div key={`key_${index}`} className={styles.Slide}>
              <Brochure download={slide} />
            </div>
          ))}
        </Carousel>
      </div>
      {slides.length > 1 && (
        <CarouselButtons
          slides={slides.map((slide, index) => ({
            active: index === currentSlide
          }))}
          onChange={(index) => setCurrentSlide(index)}
        />
      )}
    </>
  );
};

const MobileDownloadsPage = ({ downloads }) => {
  const brochureSections = [
    {
      title: "Solutions Brochures",
      brochures: [
        downloads["open-space"],
        downloads["private-space"],
        downloads["spaces-for-hire"]
      ].filter((item) => item && item.media)
    },
    {
      title: "Programming Brochures",
      brochures: [
        downloads["a-little-night-music"],
        downloads["east-room-multiples"],
        downloads["work-redux-mix"]
      ].filter((item) => item && item.media)
    },
    {
      title: "Sponsorship Package",
      brochures: [downloads["event-sponsorship"]].filter(
        (item) => item && item.media
      )
    }
  ];
  return (
    <>
      <Hero title="Downloads" />
      <section className={styles.DownloadSection}>
        <div className={styles.DownloadHeading}>
          <Heading className={styles.Heading}>Logo Types</Heading>
        </div>
        <div className={styles.DownloadSlider}>
          <LogoTypesSlider downloads={downloads} />
        </div>
      </section>

      <section className={styles.DownloadSection}>
        <div className={styles.DownloadHeading}>
          <Heading className={styles.Heading}>Wallpaper</Heading>
        </div>
        <div className={styles.DownloadSlider}>
          <WallpaperSlider downloads={downloads} />
        </div>
      </section>

      {brochureSections.map((brochureSection) => {
        if (brochureSection.brochures.length <= 0) {
          return null;
        }

        return (
          <section
            key={brochureSection.title}
            className={styles.DownloadSection}
          >
            <div className={styles.DownloadHeading}>
              <Heading className={styles.Heading}>
                {brochureSection.title}
              </Heading>
            </div>
            <div className={styles.DownloadSlider}>
              <BrochureSlider downloads={brochureSection.brochures} />
            </div>
          </section>
        );
      })}
    </>
  );
};

export default function DownloadsPage({ downloads, seo }) {
  const device = useViewport();

  return (
    <>
      <SeoMeta {...seo} />
      {device !== Viewport.PHONE && (
        <DesktopDownloadsPage downloads={downloads} />
      )}
      {device === Viewport.PHONE && (
        <MobileDownloadsPage downloads={downloads} />
      )}
    </>
  );
}

export async function getStaticProps() {
  try {
    const [downloads, seo] = await Promise.all([
      getDownloads(),
      getSEOPageProps({ resolvedUrl: "/downloads" })
    ]);

    return {
      props: { downloads, seo },
      revalidate: config.ISR_TIMING
    };
  } catch (e) {
    console.log(e.message);
  }
}
